import { authApiUrl } from './apiConfig';

const AUTH_KEYS = ['authToken', 'authTokenExpires', 'refreshToken', 'username', 'userId'];
let authFailureHandled = false;

const clearAuth = () => {
    AUTH_KEYS.forEach((key) => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
    });
};

const decodeTokenPayload = (token) => {
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized.padEnd(
            normalized.length + ((4 - normalized.length % 4) % 4),
            '=',
        );
        return JSON.parse(atob(padded));
    } catch (_error) {
        return null;
    }
};

const isInvalidOrExpired = (token, storedExpiry) => {
    const payload = decodeTokenPayload(token);
    if (!payload) return true;

    const parsedExpiry = storedExpiry ? Date.parse(storedExpiry) : NaN;
    const expiresAt = Number.isNaN(parsedExpiry)
        ? (payload.exp ? payload.exp * 1000 : null)
        : parsedExpiry;

    // Authservice всегда выдаёт JWT со сроком жизни. Неизвестный формат
    // считаем невалидным, чтобы не показывать защищённый интерфейс до первого 401.
    return !expiresAt || expiresAt <= Date.now();
};

export const getAuthToken = () => {
    const persistentToken = localStorage.getItem('authToken');
    const sessionToken = sessionStorage.getItem('authToken');
    const token = persistentToken || sessionToken;

    if (!token) return null;

    const expires = localStorage.getItem('authTokenExpires')
        || sessionStorage.getItem('authTokenExpires');
    if (isInvalidOrExpired(token, expires)) {
        clearAuth();
        return null;
    }

    // Миграция с прежнего sessionStorage: повторное открытие админки больше не
    // теряет рабочую сессию в пределах срока жизни токена.
    if (!persistentToken) {
        localStorage.setItem('authToken', token);
        ['authTokenExpires', 'username', 'userId'].forEach((key) => {
            const value = sessionStorage.getItem(key);
            if (value) localStorage.setItem(key, value);
        });
    }

    return token;
};

export const prepareInitialAuthRoute = () => {
    const hasToken = Boolean(getAuthToken());
    const isLoginRoute = window.location.hash === '#/login'
        || window.location.hash.startsWith('#/login?');

    if (!hasToken && !isLoginRoute) {
        window.history.replaceState(
            null,
            '',
            `${window.location.pathname}${window.location.search}#/login`,
        );
    }
};

const authProvider = {
    login: async ({ username, password }) => {
        const response = await fetch(`${authApiUrl}/admin/login`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, password }),
        });

        if (!response.ok) {
            if (response.status === 429) {
                throw new Error('Слишком много попыток входа. Попробуйте позже.');
            }

            const error = await response.json().catch(() => ({}));
            throw new Error(error.detail || 'Неверный логин или пароль');
        }

        const data = await response.json();
        clearAuth();
        localStorage.setItem('authToken', data.auth_token.token);
        localStorage.setItem('authTokenExpires', data.auth_token.expires);
        localStorage.setItem('username', username);
        localStorage.setItem('userId', data.user_id);
        authFailureHandled = false;
    },
    logout: () => {
        clearAuth();
        authFailureHandled = true;
        return Promise.resolve({ redirectTo: '/login' });
    },
    checkAuth: () => {
        if (getAuthToken()) return Promise.resolve();
        return Promise.reject({ redirectTo: '/login', message: false });
    },
    checkError: ({ status }) => {
        if (status === 401 || status === 403) {
            clearAuth();

            // На дашборде несколько запросов могут одновременно вернуть 401.
            // Только первый инициирует выход, остальные не создают цикл
            // редиректов и повторяющихся уведомлений.
            if (authFailureHandled) return Promise.resolve();
            authFailureHandled = true;
            return Promise.reject({ redirectTo: '/login', message: false });
        }
        return Promise.resolve();
    },
    getPermissions: () => Promise.resolve(),
    getIdentity: () => {
        const username = localStorage.getItem('username') || sessionStorage.getItem('username') || 'admin';
        return Promise.resolve({
            id: localStorage.getItem('userId') || sessionStorage.getItem('userId') || username,
            fullName: username,
        });
    },
};

export default authProvider;
